export interface SpeechRecognitionEvent extends Event {
    results: SpeechRecognitionResultList;
    resultIndex: number;
}
export interface SpeechRecognition {
    continuous: boolean;
    interimResults: boolean;
    onresult?: (event: SpeechRecognitionEvent) => void;
    onspeechend?: () => void;
    onerror?: (event: { error: ErrorMessage }) => void;
    start: () => void;
    stop: () => void;
    lang: string;
}

export type ErrorMessage = 'permission-issue' | 'no-speech' | 'vts-unsupported' | 'aborted' | 'start-issue' | 'stop-issue';

export const startSpeechRecogntion = (() => {
    let instance: SpeechRecognition | null;
    let started = false;

    let syncTimeout: ReturnType<typeof setTimeout> | null = null;

    const Constructor = window.webkitSpeechRecognition || window.SpeechRecognition;

    return (onresult: SpeechRecognition['onresult'], onerror: (event: { error: ErrorMessage }) => void) => {
        if (!Constructor) {
            return { errorMessage: 'vts-unsupported' };
        }

        const createInstance = () => {
            instance = null;
            instance = new Constructor();
            instance.continuous = true;
            instance.lang = 'en-US';
            instance.interimResults = true;
            started = false;
            syncTimeout = null;
            // Attach error handling to recreate the instance
            instance.onerror = event => {
                onerror(event); // Pass the error event to the provided handler
                recreateInstance();
            };
        };

        const recreateInstance = () => {
            createInstance();
            attachHandlers();
        };

        const attachHandlers = () => {
            if (instance) {
                instance.onresult = onresult;
                instance.onerror = event => {
                    onerror(event); // Pass the error event to the provided handler
                    recreateInstance();
                };
            }
        };

        if (!instance) {
            createInstance();
        }

        if (instance) {
            attachHandlers();
        }

        if (!started) {
            syncTimeout && clearTimeout(syncTimeout);
            syncTimeout = setTimeout(() => {
                try {
                    instance?.start();
                    started = true;
                    syncTimeout = null;
                } catch (e) {
                    onerror({ error: 'start-issue' });
                }
            }, 400);
        }

        const stop = () => {
            if (syncTimeout) {
                clearTimeout(syncTimeout);
                syncTimeout = null;
            } else if (started) {
                try {
                    instance?.stop();
                } catch (e) {
                    onerror({ error: 'stop-issue' });
                }
                started = false;
            }
        };
        return { stop };
    };
})();
